import { useAnalytics } from "@dashboard/common/analytics";
import { actions, useAppDispatch } from "@dashboard/config/redux";
import { useMemo, useRef } from "react";
import { useLocalStorage } from "@domun/component-library";
import { CoreEvents } from "@dashboard/constants/events";
import { useFormikContext } from "formik";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { toast } from "sonner";

import { EVENTS } from "@dashboard/constants/events";
import { useProducts } from "@dashboard/modules/products/hooks/products.hook";
import { PlanBenefit } from "@dashboard/entities/plan-benefit";
import { FeatureProducts } from "@dashboard/modules/products/constants/features.constants";
import {
  CLIENT_IMAGE_TOO_LARGE_MESSAGE,
  PRODUCT_IMAGE_MAX_BYTES,
  PRODUCT_IMAGE_MAX_MB,
} from "@dashboard/modules/products/constants/product-image.constants";
import {
  categorySelector,
  createUniqueProductAction,
  productsActions,
} from "@dashboard/modules/products/redux";
import {
  CreateProductPayload,
  UpdateProductPayload,
  Product,
} from "@dashboard/modules/products/types/products.types";
import {
  buildProductPayload,
  isService,
} from "@dashboard/modules/products/utils/service-form.utils";
import {
  FormProps,
  ImageProductForm,
} from "@dashboard/modules/products/types/product-form.types";
import { useNavigate } from "react-router-dom";
import { camelizeKeys } from "humps";
import { fetchUploadImages } from "@dashboard/services/file.uploader";

export const useProduct = () => {
  const UPLOAD_TIMEOUT_MS = 120000;
  const IMAGE_UPLOAD_LIMIT_MB = PRODUCT_IMAGE_MAX_MB;
  const notifyError = (message: string) => {
    toast.error(message);
    if (typeof window !== "undefined") {
      setTimeout(() => {
        const hasToaster =
          document.querySelector("[data-sonner-toaster]") ||
          document.querySelector("[data-sonner-toast]");
        if (!hasToaster) {
          window.alert(message);
        }
      }, 120);
    }
  };
  const isPayloadTooLargeError = (error: any): boolean => {
    const rawError = JSON.stringify(error || {}).toLowerCase();
    return (
      error?.response?.status === 413 ||
      error?.status === 413 ||
      error?.code === 413 ||
      error?.code === "413" ||
      rawError.includes("\"status\":413") ||
      rawError.includes("\"code\":413") ||
      rawError.includes("413") ||
      rawError.includes("payload too large") ||
      rawError.includes("request entity too large") ||
      rawError.includes("entity too large")
    );
  };
  const { t } = useTranslation();
  const [accessToken] = useLocalStorage("accessToken", null);
  const { logEvent } = useAnalytics();
  const dispatch = useAppDispatch();
  const categoriesData = useSelector(categorySelector);
  const {
    isValid,
    setFieldTouched,
    submitForm,
    resetForm,
    setFieldValue,
    setSubmitting,
  } = useFormikContext() ?? {};
  const { values }: { values: FormProps } = useFormikContext() ?? {};
  const {
    products,
    createProductsFeature,
    handleUpdateProduct: handleUpdateProductFn,
  } = useProducts();
  const [isLoading, setIsLoading] = useState(false);
  /** 0–100 mientras se guarda; null = sin overlay de progreso */
  const [saveProgressPercent, setSaveProgressPercent] = useState<number | null>(
    null
  );
  const navigate = useNavigate();
  const DEFAULTIMAGE = "https://assets.domun.co/prod/sumerweb/products/default-product-image.png";
  const DEFAULTIMAGES3 = "https://sumer-s3-database.s3.us-west-2.amazonaws.com/prod/sumerweb/products/default-product-image.png";

  const productTitleRef = useRef<HTMLDivElement>(null);
  const scrollToProductTitle = () => {
    productTitleRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const maxProductsCreate = useMemo(() => {
    return createProductsFeature
      ? (createProductsFeature as PlanBenefit).conditions?.max_catalogue_size ||
          0
      : 0;
  }, [createProductsFeature]);

  const handleSaveProduct = () => {
    if (isService(values)) {
      return;
    }
    if (!isValid) {
      setIsLoading(false);
      scrollToProductTitle();
      return;
    }
    if (values?.id) {
      handleUpdateProduct();
    } else {
      handleCreateProduct();
    }
  };

  const handleCreateProductDuplicate = (product: Product) => {
    if (product) {
      handleCreateProduct(product);
    }
  };

  const validateCountVariations = (): boolean => {
    const aux = values.productToppingVariations ?? [];
    let isModified = aux.length === 0;
    if (aux) {
      aux.forEach((topping) => {
        if (topping.inventory?.units !== 1) {
          isModified = true;
        }
      });
    }
    return isModified;
  };

  const handleCreateProduct = async (product?: Product) => {
    setFieldTouched("name");
    setFieldTouched("price");
    if (isValid) {
      if (products.length < maxProductsCreate) {
        setIsLoading(true);
        setSaveProgressPercent(0);
        submitForm();
        setSubmitting(true);
        const productToCreate: FormProps = cleanToppings(
          product ? { ...product } : { ...values }
        );
        const categoryAux = setCategory(productToCreate.category);
        try {
          setSaveProgressPercent(2);
          let images: string[] | undefined = [];
          if (product) {
            const imgForm = await createFileByUrl(
              productToCreate.imagesForm || []
            );
            images = await uploadImagesToS3({
              ...productToCreate,
              imagesForm: imgForm,
            });
          } else {
            images = await uploadImagesToS3(productToCreate);
          }
          await createNewProduct(
            buildProductPayload(productToCreate, {
              category: categoryAux || null,
              images: images || [],
              imagesDetail: (images || []).map((image) => ({ url: image })),
            }) as CreateProductPayload
          );
        } catch (error) {
          if ((error as Error)?.message === CLIENT_IMAGE_TOO_LARGE_MESSAGE) {
            notifyError(
              `La imagen es demasiado pesada. Prueba con una imagen más liviana (máx. ${IMAGE_UPLOAD_LIMIT_MB} MB).`
            );
          } else if ((error as Error)?.message === "upload_timeout") {
            notifyError(
              `La carga de la imagen tardó demasiado. Intenta con una imagen más liviana (máx. ${IMAGE_UPLOAD_LIMIT_MB} MB).`
            );
          } else if (isPayloadTooLargeError(error)) {
            notifyError(
              `La imagen es demasiado pesada. Prueba con una imagen más liviana (máx. ${IMAGE_UPLOAD_LIMIT_MB} MB).`
            );
          } else {
            notifyError(t("admin.products.side.formAlert.error"));
          }
          setIsLoading(false);
          setSubmitting(false);
          setSaveProgressPercent(null);
        }
      } else {
        handleShowMaxProductsHook();
        setIsLoading(false);
        setSubmitting(false);
        setSaveProgressPercent(null);
      }
    } else {
      setIsLoading(false);
      setSubmitting(false);
      setSaveProgressPercent(null);
    }
  };

  const uploadImagesToS3 = async (productToCreate: FormProps) => {
    if (!productToCreate.imagesForm?.length) {
      setSaveProgressPercent(75);
      return [DEFAULTIMAGE];
    }
    return await uploadFile(
      (productToCreate.imagesForm || []).map((item) => item.image as File)
    );
  };
  const createFileByUrl = async (
    imagesForm: ImageProductForm[]
  ): Promise<ImageProductForm[]> => {
    let auxProductToCreate: ImageProductForm[] = [];
    let fetchingData = await Promise.all(
      imagesForm?.map(async (item) => {
        const imgUrl = (item.image as string).includes("default-product-image")
          ? DEFAULTIMAGES3
          : (item.image as string);
        return fetch(imgUrl)
          .then((response) => response.blob())
          .then((data) => {
            const imageName = (item.image as string).split("/").pop();
            const imageExtension = imageName?.split(".").pop();
            return new File([data], imageName || "duplicate-sumer-image.jpeg", {
              type: `image/${imageExtension ? imageExtension : "jpeg"}`,
            });
          });
      }) || []
    );
    fetchingData.forEach((file, index) => {
      if (file.size > PRODUCT_IMAGE_MAX_BYTES) {
        throw new Error(CLIENT_IMAGE_TOO_LARGE_MESSAGE);
      }
      auxProductToCreate.push({
        id: index,
        image: file,
        type: "file",
      });
    });
    return auxProductToCreate;
  };

  const handleShowMaxProductsHook = () => {
    dispatch(actions.showCustomPlanHook(FeatureProducts.CreateProducts));
    logEvent(CoreEvents.SystemExpertWarning, {
      event_subtype: EVENTS.WEB_SUMER_WEB_SELECT_ADD_PRODUCT,
      source: "hook",
    });
  };

  const handleUpdateProduct = async () => {
    if (isValid) {
      setIsLoading(true);
      setSaveProgressPercent(0);
      submitForm();
      const productToEdit: FormProps = cleanToppings({ ...values });
      const categoryAux = setCategory(productToEdit.category);
      try {
        setSaveProgressPercent(2);
        const imagesUpload = (productToEdit.imagesForm || []).filter(
          (item) => item.type === "file"
        );
        const newImages = imagesUpload.length
          ? (await uploadFile(
              imagesUpload.map((item) => item.image) as File[]
            )) || []
          : [];
        if (!imagesUpload.length) {
          setSaveProgressPercent(75);
        }
        newImages.forEach((imageUrl, index) => {
          imagesUpload[index].image = imageUrl;
          imagesUpload[index].type = "url";
        });
        const images = (productToEdit.imagesForm || []).map((image) => {
          const isNewImage = imagesUpload.find(
            (newImage) => newImage.id === image.id
          );
          return (isNewImage ? isNewImage.image : image.image) as string;
        });
        setSaveProgressPercent(85);
        updateProduct(
          buildProductPayload(productToEdit, {
            category: categoryAux || null,
            images: images.length ? images : [DEFAULTIMAGE],
            imagesDetail: images.map((image) => ({ url: image })),
          }) as UpdateProductPayload
        );
      } catch (error) {
        if ((error as Error)?.message === CLIENT_IMAGE_TOO_LARGE_MESSAGE) {
          notifyError(
            `La imagen es demasiado pesada. Prueba con una imagen más liviana (máx. ${IMAGE_UPLOAD_LIMIT_MB} MB).`
          );
        } else if ((error as Error)?.message === "upload_timeout") {
          notifyError(
            `La carga de la imagen tardó demasiado. Intenta con una imagen más liviana (máx. ${IMAGE_UPLOAD_LIMIT_MB} MB).`
          );
        } else if (isPayloadTooLargeError(error)) {
          notifyError(
            `La imagen es demasiado pesada. Prueba con una imagen más liviana (máx. ${IMAGE_UPLOAD_LIMIT_MB} MB).`
          );
        } else {
          notifyError(t("admin.products.side.formAlert.error"));
        }
        setIsLoading(false);
        setSubmitting(false);
        setSaveProgressPercent(null);
      }
    } else {
      setIsLoading(false);
      setSubmitting(false);
      setSaveProgressPercent(null);
    }
  };

  const cleanToppings = (product: FormProps): FormProps => {
    if (product.toppings) {
      product.toppings = product.toppings.filter(
        (topping) => topping.id !== "new"
      );
    }
    return product;
  };

  const uploadFile = async (files: File[]) => {
    if (files.some((f) => f.size > PRODUCT_IMAGE_MAX_BYTES)) {
      throw new Error(CLIENT_IMAGE_TOO_LARGE_MESSAGE);
    }
    if (!accessToken) {
      throw new Error("missing_access_token");
    }
    if (accessToken) {
      // Una petición por archivo evita cuerpos multipart enormes (p. ej. 10×5MB) que el
      // proxy de webpack o el upstream suelen cortar con ERR_CONNECTION_RESET.
      const fromPct = 5;
      const toPct = 75;
      const span = toPct - fromPct;
      const n = files.length;
      const urls: string[] = [];

      for (let i = 0; i < n; i++) {
        const onUploadProgress = (event: ProgressEvent) => {
          if (!event.total) {
            return;
          }
          const fileFraction = event.loaded / event.total;
          const overall =
            fromPct + ((i + fileFraction) / n) * span;
          setSaveProgressPercent(
            Math.min(74, Math.max(0, Math.round(overall)))
          );
        };

        const uploadPromise = fetchUploadImages(
          accessToken,
          [
            {
              name: `image_${i}`,
              file: files[i],
            },
          ],
          onUploadProgress
        ).then((res) => camelizeKeys(res.data) as Record<string, unknown>);

        const timeoutRequest = new Promise<never>((_, reject) => {
          setTimeout(() => {
            reject(new Error("upload_timeout"));
          }, UPLOAD_TIMEOUT_MS);
        });

        const response: Record<string, unknown> = await Promise.race([
          uploadPromise,
          timeoutRequest,
        ]);
        const vals = Object.values(response).filter(
          (v): v is string => typeof v === "string" && v.startsWith("http")
        );
        const url = vals[0];
        if (!url) {
          throw new Error("upload_empty_response");
        }
        urls.push(url);
        setSaveProgressPercent(
          Math.round(fromPct + ((i + 1) / n) * span)
        );
      }
      setSaveProgressPercent(75);
      return urls;
    }
  };

  const createNewProduct = async (product: CreateProductPayload) => {
    if (!accessToken) {
      setSubmitting(false);
      setIsLoading(false);
      setSaveProgressPercent(null);
      notifyError(t("admin.products.side.formAlert.error"));
      return;
    }
    if (accessToken) {
      logEvent(EVENTS.WEB_SUMER_WEB_SELECT_ADD_PRODUCT);
      setSaveProgressPercent(78);
      try {
        const response = await dispatch(
          createUniqueProductAction({
            token: accessToken,
            payload: { product },
          })
        ).unwrap();

        resetForm();
        setFieldValue("name", "");
        setSubmitting(false);
        setSaveProgressPercent(100);

        if (response?.code === "validation.request") {
          notifyError(t("admin.products.side.formAlert.error"));
          setSaveProgressPercent(null);
          return;
        }
        if (response?.code === "prime.membership_not_active") {
          dispatch(productsActions.setShowNotAvailableHook(true));
          handleShowMaxProductsHook();
          setSaveProgressPercent(null);
          return;
        }
        if (response?.code) {
          notifyError(
            response?.message ||
              response?.error ||
              t("admin.products.side.formAlert.error")
          );
          setSaveProgressPercent(null);
          return;
        }

        navigate("../../products");
        toast.success(t("admin.products.side.formAlert.success"));
      } catch (error: any) {
        if (isPayloadTooLargeError(error)) {
          notifyError(
            `La imagen es demasiado pesada. Prueba con una imagen más liviana (máx. ${IMAGE_UPLOAD_LIMIT_MB} MB).`
          );
        } else {
          notifyError(t("admin.products.side.formAlert.error"));
        }
        setSubmitting(false);
      } finally {
        setIsLoading(false);
        setSaveProgressPercent(null);
      }
    }
  };

  const updateProduct = (product: UpdateProductPayload) => {
    if (!accessToken) {
      setIsLoading(false);
      setSubmitting(false);
      setSaveProgressPercent(null);
      notifyError(t("admin.products.side.formAlert.error"));
      return;
    }
    if (accessToken) {
      handleUpdateProductFn(product)
        ?.then(() => {
          setSaveProgressPercent(100);
          navigate("../../products");
          toast.success(t("admin.products.side.formAlert.success"));
        })
        .catch(() => {
          notifyError(t("admin.products.side.formAlert.error"));
        })
        .finally(() => {
          setIsLoading(false);
          setSubmitting(false);
          setSaveProgressPercent(null);
        });
    }
  };

  const setCategory = (category: string | null): string | null => {
    let categoryFound = categoriesData.find(
      (cat) => cat.name === category && cat.isUserCategory
    );
    return categoryFound?.id || category;
  };

  return {
    isLoading,
    setIsLoading,
    saveProgressPercent,
    setSaveProgressPercent,
    handleSaveProduct,
    validateCountVariations,
    productTitleRef,
    scrollToProductTitle,
    handleCreateProductDuplicate,
  };
};
